/**
 * Local utility to generate variants compliant with switch component. Will generate variant and variant+"/checked" entries for track and thumb.
 * @param name name of the variant without the "/checked" part
 * @param baseStyle track style for both ON and OFF state
 * @param checkedStyle delta track style for the ON state (applied to the "/checked" variant), will override baseStyle keys with same name
 * @param baseThumbStyle thumb style for both ON and OFF state
 * @param checkedThumbStyle delta thumb style for the ON state (applied to the "/checked" variant), will override baseStyle keys with same name
 * @returns object to be spread inside the style object
 */
const variantFactory = (
  name: string,
  {
    baseStyle = {},
    checkedStyle = {},
    baseThumbStyle = {},
    checkedThumbStyle = {},
  }: {
    baseStyle?: Record<string, unknown>;
    checkedStyle?: Record<string, unknown>;
    baseThumbStyle?: Record<string, unknown>;
    checkedThumbStyle?: Record<string, unknown>;
  },
) => {
  const obj = {
    [name]: {
      ...baseStyle,
      subComponents: {
        thumb: {
          ...baseThumbStyle,
        },
      },
    },
    [`${name}/checked`]: {
      ...baseStyle,
      ...checkedStyle,
      subComponents: {
        thumb: {
          ...baseThumbStyle,
          ...checkedThumbStyle,
        },
      },
    },
  };
  return obj;
};

/**
 * Switch theme configuration.
 *
 * Single source of truth for the Switch look. The component never receives
 * color/size style props: it only forwards a `variant` to the DS `Box` via
 * `componentName`, so any style added here flows automatically.
 *
 * Variant naming is composite and slash-separated: `<color>/<size>/<state>`.
 * The component appends `/checked` to the incoming variant when active.
 *   classic, classic/checked, classic/small, classic/small/checked
 *   dark,    dark/checked,    dark/small,    dark/small/checked
 *
 * `classic` (default size, unchecked) is the base style, so it needs no entry.
 * Each variant only declares the delta over the base — the DS merges base + the
 * single matched variant (it does NOT compose `dark` + `small` on its own).
 */
export const switchStyle = {
  // base = classic / unchecked / default size
  width: '71px',
  height: '40px',
  borderRadius: 'round',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  flexShrink: '0',
  transition: 'background-color 0.3s',
  backgroundColor: 'grey/light/background',

  subComponents: {
    thumb: {
      width: '34px',
      height: '34px',
      borderRadius: 'round',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      backgroundColor: 'white',
      transition: 'transform 0.3s',
    },
  },

  variants: {
    ...variantFactory('classic', {
      checkedStyle: {
        backgroundColor: 'primary',
      },
    }),

    ...variantFactory('classic/small', {
      baseStyle: {
        width: '37px',
        height: '20px',
      },
      checkedStyle: {
        backgroundColor: 'primary',
      },
      baseThumbStyle: {
        width: '16px',
        height: '16px',
      },
    }),

    ...variantFactory('darkOrLightMode', {
      baseStyle: {
        backgroundColor: 'grey/dark/background',
      },
      checkedStyle: { backgroundColor: 'primary' },
      baseThumbStyle: { backgroundColor: 'primary' },
      checkedThumbStyle: { backgroundColor: 'white' },
    }),
  },
};
